import { useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { api, ApiError, type TechnicianOption, type BranchOption } from '../lib/api'
import type { Severity } from '../../shared/types'
import { compressPhoto, isHeic } from '../lib/photo'

const MAX_PHOTOS = 8

type Load =
  | { state: 'loading' }
  | {
      state: 'ok'
      company: string
      technicians: TechnicianOption[]
      fixedTechnician: TechnicianOption | null
      branches: BranchOption[]
      fixedBranch: BranchOption | null
    }
  | { state: 'missing' }

// Mobile-first public damage report. Shared form at /f/:formToken; a per-
// technician personal link at /f/:formToken/t/:techId (pre-fills the tech).
export function PublicFormPage() {
  const { formToken = '', techId } = useParams()
  const [load, setLoad] = useState<Load>({ state: 'loading' })
  const [submitted, setSubmitted] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const request = techId
      ? api.getPersonalForm(formToken, techId).then((r) => ({
          company: r.company.name,
          technicians: [] as TechnicianOption[],
          fixedTechnician: r.technician,
          branches: r.branches,
          fixedBranch:
            r.technician.branch_id && r.technician.branch_name
              ? { id: r.technician.branch_id, name: r.technician.branch_name }
              : null,
        }))
      : api.getPublicForm(formToken).then((r) => ({
          company: r.company.name,
          technicians: r.technicians,
          fixedTechnician: null,
          branches: r.branches,
          fixedBranch: null,
        }))
    request
      .then((res) => active && setLoad({ state: 'ok', ...res }))
      .catch(() => active && setLoad({ state: 'missing' }))
    return () => {
      active = false
    }
  }, [formToken, techId])

  if (load.state === 'loading') {
    return <Centered>Loading…</Centered>
  }
  if (load.state === 'missing') {
    return (
      <Centered>
        <div className="card" style={{ maxWidth: 420, textAlign: 'center' }}>
          <div className="page-title">Form not found</div>
          <p style={{ color: 'var(--gray)', marginTop: 8 }}>
            This damage-report link is invalid or has been disabled. Please check with your
            supervisor for the correct link.
          </p>
        </div>
      </Centered>
    )
  }
  if (submitted) {
    return (
      <Centered>
        <div className="card" style={{ maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>✅</div>
          <div className="page-title" style={{ marginTop: 8 }}>Report submitted</div>
          <p style={{ color: 'var(--gray)', marginTop: 8 }}>
            Your damage report has been logged as
          </p>
          <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: '6px 0 16px' }}>
            {submitted}
          </p>
          <button className="btn btn-primary" onClick={() => setSubmitted(null)}>
            Submit another
          </button>
        </div>
      </Centered>
    )
  }

  return (
    <DamageForm
      token={formToken}
      company={load.company}
      technicians={load.technicians}
      fixedTechnician={load.fixedTechnician}
      branches={load.branches}
      fixedBranch={load.fixedBranch}
      onDone={setSubmitted}
    />
  )
}

const OTHER = '__other__'

function DamageForm({
  token,
  company,
  technicians,
  fixedTechnician,
  branches,
  fixedBranch,
  onDone,
}: {
  token: string
  company: string
  technicians: TechnicianOption[]
  fixedTechnician: TechnicianOption | null
  branches: BranchOption[]
  fixedBranch: BranchOption | null
  onDone: (caseNumber: string) => void
}) {
  const [severity, setSeverity] = useState<Severity>('minor')
  const [photos, setPhotos] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [incident] = useState(() => localNowValue())
  // Branch: a dropdown when the company uses branches and the tech isn't tied
  // to one. When the personal link's tech has a branch, it's fixed.
  const usesBranches = Boolean(fixedBranch) || branches.length > 0
  const [branchSel, setBranchSel] = useState<string>(fixedBranch?.id ?? branches[0]?.id ?? '')
  // Technician selection: a roster id, OTHER (type a name), or '' (no roster).
  const [techSel, setTechSel] = useState<string>(technicians[0]?.id ?? '')
  const [techName, setTechName] = useState('')
  // Pre-filled from the roster when we know the technician; always editable.
  const first = fixedTechnician ?? technicians[0]
  const [techPhone, setTechPhone] = useState(first?.phone ?? '')
  const [supName, setSupName] = useState(first?.supervisor_name ?? '')
  const [supPhone, setSupPhone] = useState(first?.supervisor_phone ?? '')

  const hasRoster = technicians.length > 0
  const typingName = fixedTechnician ? false : !hasRoster || techSel === OTHER

  // Swapping the selected technician swaps in their number and their supervisor.
  useEffect(() => {
    if (fixedTechnician) return
    const picked = technicians.find((t) => t.id === techSel)
    setTechPhone(picked?.phone ?? '')
    setSupName(picked?.supervisor_name ?? '')
    setSupPhone(picked?.supervisor_phone ?? '')
  }, [techSel, technicians, fixedTechnician])

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    if (photos.length === 0) {
      setError('Please add at least one photo of the damage.')
      return
    }
    if (typingName && !techName.trim()) {
      setError('Please enter the technician name.')
      return
    }
    // Branch required when the company uses branches (unless fixed by the tech).
    if (usesBranches && !fixedBranch && !branchSel) {
      setError('Please choose a branch.')
      return
    }
    setSubmitting(true)
    const fd = new FormData(e.currentTarget)
    fd.delete('photos')
    for (const p of photos) fd.append('photos', p)
    fd.set('severity', severity)
    if (usesBranches) fd.set('branch_id', fixedBranch?.id ?? branchSel)
    fd.set('technician_phone', techPhone.trim())
    fd.set('supervisor_name', supName.trim())
    fd.set('supervisor_phone', supPhone.trim())
    // Technician: id from a roster pick / personal link, else a typed name.
    if (fixedTechnician) fd.set('technician_id', fixedTechnician.id)
    else if (hasRoster && techSel !== OTHER) fd.set('technician_id', techSel)
    else fd.set('technician_name', techName.trim())
    // Retry on transient network errors (weak signal), but not on a real server
    // rejection (an ApiError means the server answered — retrying won't help).
    let lastErr: unknown
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await api.submitPublicForm(token, fd)
        onDone(res.case_number)
        setSubmitting(false)
        return
      } catch (err) {
        lastErr = err
        if (err instanceof ApiError) break
        await new Promise((r) => setTimeout(r, 700 * (attempt + 1)))
      }
    }
    setError(messageFor(lastErr))
    setSubmitting(false)
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: 24 }}>
      <div className="brand" style={{ color: 'var(--navy)', fontWeight: 700, fontSize: 22 }}>
        {company}
      </div>
      <div className="page-title" style={{ marginTop: 8 }}>Damage Report</div>
      <p className="page-sub">For emergencies, contact your supervisor, then complete this form.</p>

      <form className="card" onSubmit={onSubmit}>
        <PhotoField photos={photos} setPhotos={setPhotos} />

        {fixedTechnician ? (
          <div className="field">
            <label>Technician</label>
            <div style={{ padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', fontWeight: 600 }}>
              {fixedTechnician.name}
            </div>
          </div>
        ) : (
          <div className="field">
            <label>Technician name</label>
            {hasRoster && (
              <select value={techSel} onChange={(e) => setTechSel(e.target.value)} style={selectStyle}>
                {technicians.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
                <option value={OTHER}>Other (type a name)…</option>
              </select>
            )}
            {typingName && (
              <input
                value={techName}
                onChange={(e) => setTechName(e.target.value)}
                placeholder="Technician name"
                style={{ marginTop: hasRoster ? 8 : 0 }}
                required
              />
            )}
          </div>
        )}

        {usesBranches && (
          <div className="field">
            <label>Branch / location</label>
            {fixedBranch ? (
              <div style={{ padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', fontWeight: 600 }}>
                {fixedBranch.name}
              </div>
            ) : (
              <select value={branchSel} onChange={(e) => setBranchSel(e.target.value)} style={selectStyle} required>
                <option value="" disabled>Choose a branch…</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="field">
          <label>Technician phone number</label>
          <input
            type="tel"
            value={techPhone}
            onChange={(e) => setTechPhone(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <label>Supervisor name</label>
          <input value={supName} onChange={(e) => setSupName(e.target.value)} required />
        </div>

        <div className="field">
          <label>Supervisor phone number</label>
          <input
            type="tel"
            value={supPhone}
            onChange={(e) => setSupPhone(e.target.value)}
            required
          />
        </div>
        <Field name="customer_name" label="Customer name" required />
        <Field name="customer_phone" label="Customer phone number" type="tel" required />
        <Field name="address" label="Address where damage occurred" required />

        <div className="field">
          <label>Date of damage</label>
          <input type="datetime-local" name="incident_date" defaultValue={incident} required />
        </div>

        <div className="field">
          <label>Description of damage</label>
          <textarea name="description" required rows={4} style={textareaStyle} />
        </div>

        <div className="field">
          <label>Severity</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['minor', 'moderate', 'major'] as Severity[]).map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => setSeverity(s)}
                className="btn"
                style={severityBtn(s, severity === s)}
              >
                {s[0].toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="form-error">{error}</p>}

        <button
          className="btn btn-primary"
          type="submit"
          disabled={submitting}
          style={{ width: '100%', marginTop: 8 }}
        >
          {submitting ? 'Submitting…' : 'Submit report'}
        </button>
      </form>
    </div>
  )
}

const photoKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`

function PhotoField({
  photos,
  setPhotos,
}: {
  photos: File[]
  setPhotos: (p: File[]) => void
}) {
  // Object URLs for thumbnails; revoked when the selection changes/unmounts.
  const [urls, setUrls] = useState<string[]>([])
  useEffect(() => {
    const next = photos.map((f) => URL.createObjectURL(f))
    setUrls(next)
    return () => next.forEach((u) => URL.revokeObjectURL(u))
  }, [photos])

  const [optimizing, setOptimizing] = useState(false)

  // Add new picks to the existing selection (dedup, image-only, capped). Each
  // photo is downscaled/compressed in the browser first so it uploads reliably
  // from a phone on any connection.
  async function addFiles(files: File[]) {
    const incoming = files.filter((f) => f.type.startsWith('image/') || isHeic(f))
    if (incoming.length === 0) return
    setOptimizing(true)
    try {
      const compressed = await Promise.all(incoming.map((f) => compressPhoto(f)))
      const seen = new Set(photos.map(photoKey))
      const merged = [...photos]
      for (const f of compressed) {
        const k = photoKey(f)
        if (!seen.has(k)) {
          seen.add(k)
          merged.push(f)
        }
      }
      setPhotos(merged.slice(0, MAX_PHOTOS))
    } finally {
      setOptimizing(false)
    }
  }

  const atMax = photos.length >= MAX_PHOTOS

  return (
    <div className="field">
      <label>Photos of damage (required)</label>

      {photos.length > 0 && (
        <div className="photo-previews">
          {photos.map((f, i) => (
            <div className="photo-thumb" key={photoKey(f)}>
              {urls[i] && <img src={urls[i]} alt={`Photo ${i + 1}`} />}
              <button
                type="button"
                className="photo-del"
                title="Remove photo"
                onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      {!atMax && (
        <label className="add-photos-btn" style={optimizing ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            disabled={optimizing}
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []))
              e.currentTarget.value = '' // allow re-picking the same file / adding more
            }}
          />
          {optimizing ? 'Optimizing photo…' : photos.length ? '+ Add more photos' : '+ Add photos'}
        </label>
      )}

      <p className="photo-hint">
        {photos.length}/{MAX_PHOTOS} selected · take a photo or choose from your library
        {atMax ? ` — maximum ${MAX_PHOTOS}` : ''}
      </p>
    </div>
  )
}

function Field({
  name,
  label,
  type = 'text',
  required,
}: {
  name: string
  label: string
  type?: string
  required?: boolean
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type={type} name={name} required={required} />
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24 }}>
      {children}
    </div>
  )
}

function localNowValue(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'photo_required':
        return 'Please add at least one photo of the damage.'
      case 'too_many_photos':
        return `Too many photos — maximum ${MAX_PHOTOS}.`
      case 'photo_too_large':
        return 'One of the photos is too large (max 8 MB each).'
      case 'invalid_photo_type':
        return 'Only image files are allowed.'
      case 'missing_fields':
        return 'Please fill in all required fields.'
      case 'branch_required':
      case 'invalid_branch':
        return 'Please choose a branch, then submit again. If it keeps happening, reopen the form link to refresh it.'
      case 'form_not_found':
        return 'This form link is no longer valid.'
    }
    return 'Something went wrong submitting the report. Please try again.'
  }
  // Not an ApiError → the request didn't reach the server (network/upload issue).
  return 'Upload failed — this usually means a weak signal. Move to a better connection or Wi-Fi and try again.'
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
  resize: 'vertical',
}

function severityBtn(s: Severity, active: boolean): React.CSSProperties {
  const color =
    s === 'major' ? 'var(--sev-major)' : s === 'moderate' ? 'var(--sev-moderate)' : 'var(--sev-minor)'
  return {
    flex: 1,
    border: `1px solid ${active ? color : 'var(--line)'}`,
    background: active ? color : 'var(--surface)',
    // Moderate is yellow — needs dark text when active.
    color: active ? (s === 'moderate' ? 'var(--ink)' : '#fff') : 'var(--ink)',
  }
}
